// ===========================================
// One-off discount fields — inside the amend dialog (2026-09-28)
// ===========================================
// The same one-off discount a draft invoice takes (InvoiceDiscountApprovalModal):
// a label, a % or $ amount, and an optional "save for future invoices". Here it
// is applied AS PART OF the amendment, so it is recorded in the amendment's
// before/after and the family's email shows the discounted total. Only offered
// to someone who may apply discounts (operations_pricing:write) — the server
// refuses it otherwise.
//
// #625: it also says what it covers (DiscountCoverageFields), starting from the school default,
// and the "takes off about" figure counts only the on-screen lines it covers.

import { Box, Checkbox, FormControlLabel, InputAdornment, Switch, TextField, Typography } from '@mui/material';
import type { DiscountCoverage } from '../../types/discount.types';
import DiscountCoverageFields from '../admin/discounts/DiscountCoverageFields';
import {
  COVERS_EVERYTHING,
  coverageIsEmpty,
  estimateNewDiscount,
  type EstimateLine,
} from '../../utils/discountCoverage';

export interface AmendDiscountValue {
  name: string;
  /** Held as a STRING so the field can be emptied while retyping. */
  value: string;
  isPercent: boolean;
  saveToCustomDiscounts: boolean;
  /** #625: what it covers. Start it from the school default (`withCoverage`). */
  coverage: DiscountCoverage;
  /** Sent only when the admin changed it (/qa phase 5 L2): untouched, the server applies the default. */
  coverageTouched: boolean;
}

export const EMPTY_AMEND_DISCOUNT: AmendDiscountValue = {
  name: '',
  value: '',
  isPercent: true,
  saveToCustomDiscounts: false,
  coverage: COVERS_EVERYTHING,
  coverageTouched: false,
};

/** A fresh value covering `coverage` (the school default), never sharing its category list. */
export function withCoverage(coverage: DiscountCoverage | undefined): AmendDiscountValue {
  const c = coverage ?? COVERS_EVERYTHING;
  return { ...EMPTY_AMEND_DISCOUNT, coverage: { ...c, coversCategoryIds: [...c.coversCategoryIds] } };
}

/**
 * Valid = a label, a positive amount (a percentage no more than 100, the server's ceiling), and
 * something covered (the server's DISCOUNT_NO_COVERAGE).
 */
export function amendDiscountIsValid(d: AmendDiscountValue): boolean {
  const v = parseFloat(d.value);
  return (
    d.name.trim().length > 0 && Number.isFinite(v) && v > 0 && (!d.isPercent || v <= 100) && !coverageIsEmpty(d.coverage)
  );
}

interface AmendDiscountFieldsProps {
  value: AmendDiscountValue;
  onChange: (next: AmendDiscountValue) => void;
  /** The on-screen lines (kind, category, amount), for the "takes off about" figure. */
  lines: EstimateLine[];
  /** What the discounts the invoice keeps already take off. */
  alreadyOff?: number;
  /** The school default, for the "Use the school default" link. */
  defaultCoverage?: DiscountCoverage;
  /** The category list needs operations_pricing:read, which an invoice writer may lack. */
  canReadCategories?: boolean;
}

export default function AmendDiscountFields({
  value,
  onChange,
  lines,
  alreadyOff = 0,
  defaultCoverage,
  canReadCategories = true,
}: AmendDiscountFieldsProps) {
  const parsed = parseFloat(value.value);
  const amountIsValid = Number.isFinite(parsed) && parsed > 0 && (!value.isPercent || parsed <= 100);
  const estimate =
    amountIsValid && !coverageIsEmpty(value.coverage)
      ? estimateNewDiscount(lines, value.coverage, parsed, value.isPercent, alreadyOff)
      : null;
  const preview = estimate?.amount ?? null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <TextField
          sx={{ flex: '1 1 200px' }}
          size="small"
          label="Discount label"
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          inputProps={{ maxLength: 200 }}
        />
        <TextField
          sx={{ width: 130 }}
          size="small"
          label="Amount"
          value={value.value}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
          error={value.value.trim() !== '' && !amountIsValid}
          helperText={
            value.value.trim() !== '' && !amountIsValid
              ? value.isPercent
                ? 'Between 0 and 100'
                : 'Must be above 0'
              : undefined
          }
          InputProps={{
            startAdornment: !value.isPercent ? <InputAdornment position="start">$</InputAdornment> : undefined,
            endAdornment: value.isPercent ? <InputAdornment position="end">%</InputAdornment> : undefined,
          }}
        />
        <FormControlLabel
          control={
            <Switch checked={value.isPercent} onChange={(e) => onChange({ ...value, isPercent: e.target.checked })} />
          }
          label={value.isPercent ? 'Percentage' : 'Fixed amount'}
        />
      </Box>
      <DiscountCoverageFields
        value={value.coverage}
        onChange={(coverage) => onChange({ ...value, coverage, coverageTouched: true })}
        defaultCoverage={defaultCoverage}
        canReadCategories={canReadCategories}
      />
      {preview !== null && (
        <Typography variant="body2" color="text.secondary" data-testid="amend-discount-estimate">
          Takes off about <strong>${preview.toFixed(2)}</strong>
          {estimate?.capped && ' — capped at what the lines it covers have left'}
          {alreadyOff > 0 && ' (less if a discount already on the invoice covers the same lines)'}. The final figure
          is shown after saving.
        </Typography>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={value.saveToCustomDiscounts}
            onChange={(e) => onChange({ ...value, saveToCustomDiscounts: e.target.checked })}
          />
        }
        label="Also save it as a reusable discount for future invoices"
      />
    </Box>
  );
}
